import type React from "react";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useClickAway } from "react-use";
import { zodResolver } from "@hookform/resolvers/zod";
import { InputAdornment } from "@mui/material";
import classNames from "classnames";
import { z } from "zod";

import { ContactStatus, type IEmail } from "@/api/interfaces/userLayouts";
import { Form, FormCheckbox, FormTextField } from "@/base/components/Form";
import { Button, P } from "@/base/components/Global";
import { Col } from "@/base/components/Layout";
import Row from "@/base/components/Layout/Row";
import ConfirmationModal from "@/base/components/Modal/ConfirmationModal";
import { useUser } from "@/base/components/UserProvider";
import { VerifyEmailButton } from "@/base/components/VerifyEmailButton/VerifyEmailButton";
import { GOOGLE_ANALYTICS_ATTRIBUTES } from "@/constants/googleAnalytics";
import { createUseStyles } from "@/theme";

import type { EmailsPageModalTypes } from "../EmailsPage";

const useStyles = createUseStyles(({ spacing, color }) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: spacing.m,
  },
  notVerifiedField: {
    "& .MuiInputBase-input": {
      color: `${color.gray3} !important`,
    },
    "& .MuiFormHelperText-root": {
      color: `${color.gray4} !important`,
    },
  },
}));

type EditEmailProps = {
  setModal: (modal: EmailsPageModalTypes | null) => void;
} & IEmail;

const EmailSchema = z
  .object({
    currentEmail: z.string().email(),
    confirmEmail: z.string().optional(),
    updateToBePrimary: z.boolean(),
  })
  .refine(
    ({ currentEmail, confirmEmail }) =>
      confirmEmail ? currentEmail === confirmEmail : true,
    {
      params: { i18n: { key: "errors.emails_do_not_match" } },
      path: ["confirmEmail"],
    },
  );

type EmailData = z.infer<typeof EmailSchema>;

const EditEmail: React.FC<EditEmailProps> = ({
  id,
  email,
  status,
  isPrimary,
  setModal,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const inputRef = useRef<HTMLDivElement>(null);
  const [isActive, setIsActive] = useState(false);
  const [shouldShowDeleteModal, setShowDeleteModal] = useState(false);

  const [{ removeEmail, updateEmail, setPrimaryEmail }] = useUser();

  const form = useForm<EmailData>({
    resolver: zodResolver(EmailSchema),
    mode: "onChange",
    defaultValues: {
      currentEmail: email,
      confirmEmail: "",
      updateToBePrimary: false,
    },
  });

  const {
    watch,
    formState: { isDirty, isSubmitting, isValid },
    getFieldState,
  } = form;

  const updateToBePrimary = watch("updateToBePrimary");
  const emailFieldState = getFieldState("currentEmail");

  const onSubmit = async ({ currentEmail, updateToBePrimary }: EmailData) => {
    const isSuccess = await updateEmail({
      id,
      email: currentEmail,
      isPrimary: updateToBePrimary,
    });

    const isSuccessPrimary = updateToBePrimary
      ? await setPrimaryEmail(id)
      : true;

    setIsActive(false);

    if (isSuccess && isSuccessPrimary) {
      setModal(updateToBePrimary ? "primarySuccessModal" : "editSuccessModal");
    } else {
      setModal(updateToBePrimary ? "primaryErrorModal" : "editErrorModal");
    }
  };

  const deleteEmail = async () => {
    const isSuccess = await removeEmail(id);
    if (isSuccess) {
      setModal("removeSuccessModal");
    } else {
      setModal("removeErrorModal");
    }
  };

  useClickAway(inputRef, () => {
    setIsActive(false);
  });

  const shouldShowControls =
    !isPrimary && (isActive || isDirty || updateToBePrimary);

  const isVerified = status === ContactStatus.ACTIVE;

  const canVerifyEmail = !isVerified && !isDirty;

  const endAdornment = canVerifyEmail ? (
    <InputAdornment position="end">
      <VerifyEmailButton
        email={{
          id,
          email,
          status,
          isPrimary,
        }}
        analyticsAttributes={
          GOOGLE_ANALYTICS_ATTRIBUTES.VERIFY_EMAIL_EMAILS_LIST
        }
      />
    </InputAdornment>
  ) : null;

  return (
    <>
      <ConfirmationModal
        isOpen={shouldShowDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={t("email_delete_confirmation_title")}
        content={t("email_delete_confirmation_content")}
        primaryAction={deleteEmail}
        primaryTitle={t("email_delete_button")}
      />
      <div
        onClick={() => setIsActive(true)}
        ref={inputRef}
        className={classes.container}
      >
        <Form {...form} onSubmitValid={onSubmit}>
          <Col>
            <FormTextField
              id="email-input"
              name="currentEmail"
              autoComplete="email"
              placeholder={t("field_email_placeholder")}
              endAdornment={endAdornment}
              className={classNames([!isVerified && classes.notVerifiedField])}
              label={
                isPrimary
                  ? t("field_edit_primary_email_label")
                  : t("field_edit_secondary_email_label")
              }
              disabled={isPrimary}
              readOnly={isVerified}
            />
          </Col>
          {emailFieldState.isDirty && (
            <Col>
              <FormTextField
                label={t("field_edit_repeat_email_label")}
                id="confirm-email-input"
                name="confirmEmail"
                autoComplete="email"
                placeholder={t("field_email_placeholder")}
                className={classNames([isVerified && classes.notVerifiedField])}
              />
            </Col>
          )}
          {shouldShowControls && isVerified && (
            <Col col={12} directionColumn noVerticalMargin>
              <FormCheckbox
                label={t("checkbox_email_use_as_primary")}
                id="email-use-as-primary"
                name="updateToBePrimary"
              />
              <P xxSmall gray3 noMargin>
                {t("checkbox_email_helper_text")}
              </P>
            </Col>
          )}
          {shouldShowControls && (
            <Row noMargin>
              <Col col={6}>
                <Button
                  isDisabled={isSubmitting}
                  transparentBrand
                  action={() => setShowDeleteModal(true)}
                >
                  {t("delete_email_btn")}
                </Button>
              </Col>
              <Col col={6}>
                <Button
                  type="submit"
                  isLoading={isSubmitting}
                  isDisabled={!isValid || !isDirty}
                  analyticsAttributes={GOOGLE_ANALYTICS_ATTRIBUTES.EMAIL_CHANGE}
                >
                  {t("action_save")}
                </Button>
              </Col>
            </Row>
          )}
        </Form>
      </div>
    </>
  );
};

export default EditEmail;
